// External libraries
import React, { useCallback, useMemo, useState } from 'react'
import { Box, Flex, Input, useDisclosure, useToast } from '@chakra-ui/core'
import { useDispatch, useSelector } from 'react-redux'

// Icons
import { MdModeEditOutline } from 'react-icons/md'
import { RefreshCcw } from 'lucide-react'

// Project components
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { PopCreatorText } from 'components/PopOverview/PopCreatorText'
import { MarkUpText } from 'components/PopOverview/MarkUpText'
import GroupsTree from 'components/CommonComponents/GroupsTree'
import { UsersList } from 'components/CommonComponents/UsersListModal'
import { TagsList } from 'components/CommonComponents/TagsListModal'
import AudiencePublicationsModal from 'components/CommonComponents/AudiencePublicationModal'

// Hooks and utils
import useLocaleText from 'components/useLocaleText'
import { useAudienceState } from 'hooks/useAudienceState'

// Constants and types
import { IPop, RootState } from 'constants/interfaces'
import colors from 'constants/colors'
import strings from 'constants/strings'
import { OptionType } from 'components/NewsFeed/constants'
import { AppDispatch } from 'config/redux'
import { editPop } from 'redux/actions/pops'
import { isRtlSelector } from 'redux/selectors/general'
import { STOREE_AI_UID } from 'constants/baseValues'

interface IProps {
  aiRepop: IPop
  editMode: boolean
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
}

export const AiRepopBody = ({ aiRepop, editMode, setEditMode }: IProps) => {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()
  const isRtl = useSelector(isRtlSelector)
  const { pop_initiator } = aiRepop
  const t_save = useLocaleText('save')
  const pop_recipients = useLocaleText('pop_recipients')
  const t_topic = useLocaleText('topic')

  const [title, setTitle] = useState(aiRepop.pop_title)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const {
    config: { groups, user_tags, tags },
    activeGroupID,
  } = useSelector((state: RootState) => state.config)
  const { isLoading } = useSelector((state: RootState) => state.pops)

  const handleEditClick = () => setEditMode(true)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setEditMode(false)
    }
  }
  const handleRevert = () => {
    setTitle(aiRepop.pop_title)
    setSelectedUsersUids(aiRepop.repop_users ?? [])
    setSelectedGroupsIds(aiRepop.repop_groups ?? [])
    setSelectedTagsIds(aiRepop.repop_tags ?? [])
    setEditMode(false)
  }

  const handleSave = async () => {
    if ((selectedUsersUids.length === 0 && selectedTagsIds.length === 0 && selectedGroupsIds.length === 0) || !title) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const body = {
      pop_id: aiRepop.pk,
      data: {
        pop_title: title,
        repop_users: selectedUsersUids,
        repop_groups: selectedGroupsIds,
        repop_tags: selectedTagsIds,
      },
    }

    const res = await dispatch(editPop({ body }))
    if (res) {
      setEditMode(false)
    }
  }

  const {
    selectedUsersUids,
    setSelectedUsersUids,
    selectedGroupsIds,
    selectedTagsIds,
    setSelectedGroupsIds,
    setSelectedTagsIds,
    groupsToPayload,
    setGroupsToPayload,
    filteredRerailUsersByTreeUsers,
    treeUsers,
    treeGroups,
  } = useAudienceState({
    users: aiRepop?.repop_users,
    groups: aiRepop?.repop_groups,
    tagIds: aiRepop?.repop_tags,
    selectedOptionType: OptionType.POP,
  })

  // Memoized function to get tags list
  const tagsListToRender = useMemo(() => {
    const usersTags = user_tags ?? []
    const currentGroupTags = groups?.[activeGroupID!]?.tags ?? []
    const allTags = Array.from(new Set([...usersTags, ...currentGroupTags]))
    return tags.filter((tag) => allTags.includes(tag.sk))
  }, [user_tags, groups, activeGroupID, tags])

  // Memoization for renderViewOptionModal
  const renderViewOptionModal = useCallback(
    (viewOption: number, setViewOption: React.Dispatch<React.SetStateAction<number>> | any) => {
      switch (viewOption) {
        case 0:
          return (
            <GroupsTree
              isOpen={isOpen}
              selectedGroupID={selectedGroupsIds}
              mode={strings.PARTIAL_MULTILPE_MODE}
              setViewOption={setViewOption}
              setSelectedGroupID={setSelectedGroupsIds}
              setGroupsToPayload={setGroupsToPayload}
              groupsToPayload={groupsToPayload}
              isAllowSubmitWhithoutGroups={true}
              treeGroups={treeGroups}
            />
          )
        case 1:
          return (
            <UsersList
              isOpen={isOpen}
              onClose={onClose}
              setViewOption={setViewOption}
              selectedUsersUids={selectedUsersUids}
              setSelectedUsersUids={setSelectedUsersUids}
              treeUsers={filteredRerailUsersByTreeUsers}
            />
          )
        case 2:
          return (
            <TagsList
              isOpen={true}
              onClose={onClose}
              setViewOption={setViewOption}
              tags={tagsListToRender}
              selectedTagsIds={selectedTagsIds}
              setSelectedTagsIds={setSelectedTagsIds}
            />
          )
        default:
          return undefined
      }
    },
    [
      isOpen,
      onClose,
      selectedGroupsIds,
      setSelectedGroupsIds,
      setGroupsToPayload,
      groupsToPayload,
      treeGroups,
      selectedUsersUids,
      setSelectedUsersUids,
      filteredRerailUsersByTreeUsers,
      selectedTagsIds,
      setSelectedTagsIds,
      tagsListToRender,
    ]
  )

  return (
    <Flex
      flexDir="column"
      alignItems="center"
      px="65px"
      textAlign="center"
      pt="60px"
      flex="1"
      height="100%"
      position="relative"
    >
      {editMode && (
        <>
          <NoFocusButton
            position="absolute"
            top="-10px"
            isLoading={isLoading}
            isDisabled={isLoading}
            right={isRtl ? '0' : 'unset'}
            left={isRtl ? 'unset' : '0'}
            px="5px"
            py="3px"
            minWidth="100px"
            color="black"
            fontWeight="400"
            backgroundColor={colors.greyLight5}
            onClick={handleSave}
          >
            {t_save}
          </NoFocusButton>

          <NoFocusButton
            position="absolute"
            top="40px"
            right={isRtl ? 'unset' : '0'}
            left={isRtl ? '0' : 'unset'}
            px="5px"
            py="3px"
            minWidth="100px"
            color="black"
            fontWeight="400"
            backgroundColor={colors.greyLight5}
            onClick={onOpen}
          >
            {pop_recipients}
          </NoFocusButton>
        </>
      )}

      <NoFocusButton
        position="absolute"
        top="-10px"
        right={isRtl ? 'unset' : '0'}
        left={isRtl ? '0' : 'unset'}
        borderRadius="full"
        minWidth="unset"
        p="3px"
        minH="unset"
        w="40px"
        h="40px"
        onClick={editMode ? handleRevert : handleEditClick}
      >
        {editMode ? <RefreshCcw size={24} strokeWidth={2.5} /> : <MdModeEditOutline size={24} />}
      </NoFocusButton>

      <PopCreatorText created_by={STOREE_AI_UID} />
      {editMode ? (
        <Box wordBreak="break-word" whiteSpace="inherit" pt="50px" textAlign={isRtl ? 'right' : 'left'} w="100%">
          <Input
            value={title}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoFocus
            maxLength={60}
            style={{
              border: 'none',
              outline: 'none',
              background: 'none',
              boxShadow: 'none',
              width: '100%',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              fontSize: '24px',
              fontWeight: 400,
              fontFamily: 'Arial',
              lineHeight: '25px',
              resize: 'none',
            }}
          />
        </Box>
      ) : (
        <Flex pt="50px" w="100%" alignItems="baseline" dir={isRtl ? 'rtl' : 'ltr'} style={{ gap: '8px' }}>
          <Box as="span" fontSize="18px" fontWeight="bold" flexShrink={0} style={{ whiteSpace: 'nowrap' }}>
            {t_topic}
          </Box>
          <Box
            flex={1}
            minW={0}
            overflow="hidden"
            textAlign={isRtl ? 'right' : 'left'}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            <MarkUpText
              text={title}
              initiator={pop_initiator}
              fontFamily="Arial"
              lineHeight="25px"
              fontSize="24px"
              fontWeight="400"
            />
          </Box>
        </Flex>
      )}
      <AudiencePublicationsModal
        isOpen={isOpen}
        groups={treeGroups}
        users={treeUsers}
        onClose={onClose}
        title={pop_recipients}
        renderViewOptionModal={renderViewOptionModal}
        selectedUsersUids={selectedUsersUids}
        groupsToPayload={selectedGroupsIds ?? []}
        selectedTagsIds={selectedTagsIds ?? []}
        selectedOptionType="30"
      />
    </Flex>
  )
}
